import { get } from "lodash";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import * as yup from "yup";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Link } from "react-router-dom";
import { Message } from "primereact/message";
import { Password } from "primereact/password";
import { Title } from "components/Title";
import useApp from "hooks/useApp";
import useAuth from "hooks/useAuth";
import usePostForm from "hooks/usePostForm";
import { useTranslation } from 'react-i18next';
import LicenseFileUploader from "./LicenseFileUploader";

export default function IndexPage() {
  const auth = useAuth();
  const app = useApp();
  const { t } = useTranslation();
  const VALIDATION_IN_PROGRESS = t("VALIDATION_IN_PROGRESS");
const REGISTER_ADMIN = t("REGISTER_ADMIN");
const ACCOUNT_LOGIN = t("ACCOUNT_LOGIN");
const VALIDATE_LICENSE_KEY = t("VALIDATE_LICENSE_KEY");
const ADMIN_USER_INFO = t("ADMIN_USER_INFO");
const PASSWORD = t("PASSWORD");
const USER_NAME = t("USER_NAME");
const SERVER = t("SERVER");
const PORT = t("PORT");
const USE_TLS = t("USE_TLS");
const USE_SSL = t("USE_SSL");
const DEFAULT_SENDER_EMAIL = t("DEFAULT_SENDER_EMAIL");
const USER_NAME_OR_EMAIL = t("USER_NAME_OR_EMAIL");
const REMEMBER_ME = t("REMEMBER_ME");
const RESET_PASSWORD = t("RESET_PASSWORD");
const FINGER_PRINT = t("FINGER_PRINT");
const OFFLINE_MODE = t("OFFLINE_MODE");
const CONTACT_ADMINISTRATOR_LICENSE = t("CONTACT_ADMINISTRATOR_LICENSE");
const LICENSE_KEY = t("LICENSE_KEY");
const IP_ADDRESS = t("IP_ADDRESS");
const DB_URL = t("DB_URL");
const USER=t("USER");
const EMAIL=t("EMAIL")
const REGISTER = t("REGISTER");
const LOGIN = t("LOGIN");
const UPLOAD_AND_VALIDATE = t("UPLOAD_AND_VALIDATE");
const VALIDATE = t("VALIDATE");
const SMTP_SERVER_INFO=t("SMTP_SERVER_INFO")



  const [searchParams] = useSearchParams();
  const [rememberUser, setRememberUser] = useState(false);
  const [copyIconClass, setCopyIconClass] = useState("pi pi-clone");
  const [initialLoadComplete, setInitialLoadComplete] = useState(false); 
  const [formSubmitted, setFormSubmitted] = useState(false);

  const formUrl = "auth/login";
  let formData = {
    username: "",
    password: "",
    licenseKey: "",
    ipAddress: "",
    dbUrl: "",
    registerUser: "",
    registerEmail: "",
    registerPassword: "",
    mailUsername: "",
    mailPassword: "",
    mailServer: "",
    mailPort: "",
    mailUseTLS: false,
    mailUseSSL: false,
    mailDefaultSender: "",
  };

  const validationSchema = yup.object().shape({
    username: yup.string().label(`Username or Email`),
    password: yup.string().label(`Password`),
    licenseKey: yup.string().label(`License Key`),
    ipAddress: yup.string().label(`IP Address`),
    dbUrl: yup.string().label(`DB URL`),
    registerUser: yup.string().label(`User`),
    registerEmail: yup.string().label(`Email`),
    registerPassword: yup.string().label(`Password`),
    mailUsername: yup.string().label(`Username`),
    mailPassword: yup.string().label(`Password`),
    mailServer: yup.string().label(`Server`),
    mailPort: yup.number().label(`Port`),
    mailUseTLS: yup.boolean().label(`Use TLS`),
    mailUseSSL: yup.boolean().label(`Use SSL`),
    mailDefaultSender: yup.string().label(`Default Sender Email`),
  });

  function afterSubmit(res) {
    if (res?.error) {
      setErrorMsg(res.error);
    } else if (res?.token) {
      auth.login(res.token);
      const returnUrl = searchParams.get("redirect") || "/home";
      app.navigate(returnUrl);
    } else if (res?.nextpage) {
      app.navigate(res.nextpage);
    }
  }  

  const form = {
    formUrl,
    formData,
    validationSchema,
    afterSubmit,
  };

  const {
    loading,
    errorMsg,
    setErrorMsg,
    formik,
    validationInProgress,
    isLicenseKeyValid,
    areSMTPDetailsNeeded,
    fingerprint,
    connection,
    errorHandler,
    validationCheckSuccess,
    validationCheckError,
    setValidationInProgress,
  } = usePostForm(form, true);

  useEffect(() => {
    if (!validationInProgress) {
      setInitialLoadComplete(true);
    }
  }, [validationInProgress]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit();
    setFormSubmitted(true);
  };

  return (
    <main id="IndexPage" className="main-page">
      <section className="page-section mb-3">
        <div className="container-fluid">
          <div className="grid justify-content-center">
            <div className="col-12 sm:col-6 md:col-3 comp-grid">
              {!validationInProgress && (
                <>
                  <Title
                    title={
                      areSMTPDetailsNeeded
                        ? REGISTER_ADMIN
                        : isLicenseKeyValid
                        ? ACCOUNT_LOGIN
                        : VALIDATE_LICENSE_KEY
                    }
                    headerClass="none"
                    titleClass="text-2xl font-bold text-primary"
                    subTitleClass="text-500"
                    avatarSize="large"
                    avatarClass="bg-primary"
                    separator={false}
                  />
                  <br />
                </>
              )}
              <div className="none">
                <form onSubmit={handleFormSubmit}>
                  {validationInProgress ? (
                    <div className="mb-5 mt-2 flex justify-content-center">
                      <span className="text-900 text-xl font-medium text-primary">
                        {VALIDATION_IN_PROGRESS}
                      </span>
                      <ProgressSpinner
                        style={{
                          width: "40px",
                          height: "40px",
                          marginLeft: "20px",
                          marginRight: "20px",
                        }}
                      />
                    </div>
                  ) : areSMTPDetailsNeeded ? (
                    <>
                      <div className="mb-2">
                        <span className="text-400 font-medium">
                          {ADMIN_USER_INFO}
                        </span>
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-user"></i>
                        <InputText
                          name="registerUser"
                          id="registerUser"
                          label="User"
                          placeholder={USER}
                          className="w-full"
                          value={formik.values.registerUser}
                          onChange={formik.handleChange}
                          required
                          type="text"
                        />
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-envelope"></i>
                        <InputText
                          name="registerEmail"
                          id="registerEmail"
                          label="Email"
                          placeholder={EMAIL}
                          className="w-full"
                          value={formik.values.registerEmail}
                          onChange={formik.handleChange}
                          required
                          type="text"
                        />
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-lock"></i>
                        <Password
                          name="registerPassword"
                          id="registerPassword"
                          value={formik.values.registerPassword}
                          onChange={formik.handleChange}
                          label="Password"
                          inputClassName="w-full"
                          feedback={false}
                          toggleMask
                          className="w-full"
                          placeholder={PASSWORD}
                          required
                        />
                      </div>
                      <div className="mb-2">
                        <span className="text-400 font-medium">
                          {SMTP_SERVER_INFO}
                        </span>
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-user"></i>
                        <InputText
                          name="mailUsername"
                          id="mailUsername"
                          label="Username"
                          placeholder={USER_NAME}
                          className="w-full"
                          value={formik.values.mailUsername}
                          onChange={formik.handleChange}
                          type="text"
                        />
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-lock"></i>
                        <Password
                          name="mailPassword"
                          id="mailPassword"
                          value={formik.values.mailPassword}
                          onChange={formik.handleChange}
                          label="Password"
                          inputClassName="w-full"
                          feedback={false}
                          toggleMask
                          className="w-full"
                          placeholder={PASSWORD}
                        />
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-server"></i>
                        <InputText
                          name="mailServer"
                          id="mailServer"
                          label="Server"
                          placeholder={SERVER}
                          className="w-full"
                          value={formik.values.mailServer}
                          onChange={formik.handleChange}
                          type="text"
                        />
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-sitemap"></i>
                        <InputText
                          name="mailPort"
                          id="mailPort"
                          label="Port"
                          placeholder={PORT}
                          className="w-full"
                          value={formik.values.mailPort}
                          onChange={formik.handleChange}
                          type="number"
                        />
                      </div>
                      <div className="flex justify-content-between align-items-center my-2">
                        <div className="field-checkbox">
                          <Checkbox
                            inputId="mailUseTLS"
                            checked={formik.values.mailUseTLS}
                            onChange={() =>
                              formik.setFieldValue(
                                "mailUseTLS",
                                !formik.values.mailUseTLS
                              )
                            }
                          />
                          <label
                            className="text-l text-500"
                            htmlFor="mailUseTLS"
                          >
                            {USE_TLS}
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-content-between align-items-center my-2">
                        <div className="field-checkbox">
                          <Checkbox
                            inputId="mailUseSSL"
                            checked={formik.values.mailUseSSL}
                            onChange={() =>
                              formik.setFieldValue(
                                "mailUseSSL",
                                !formik.values.mailUseSSL
                              )
                            }
                          />
                          <label
                            className="text-l text-500"
                            htmlFor="mailUseSSL"
                          >
                            {USE_SSL}
                          </label>
                        </div>
                      </div>
                      <div className="mb-4 p-input-icon-left w-full">
                        <i className="pi pi-envelope"></i>
                        <InputText
                          name="mailDefaultSender"
                          id="mailDefaultSender"
                          label="Default Sender Email"
                          placeholder={DEFAULT_SENDER_EMAIL}
                          className="w-full"
                          value={formik.values.mailDefaultSender}
                          onChange={formik.handleChange}
                          type="text"
                        />
                      </div>
                    </>
                  ) : isLicenseKeyValid ? (
                    <>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-user"></i>
                        <InputText
                          name="username"
                          id="username"
                          label="Username Or Email"
                          placeholder={USER_NAME_OR_EMAIL}
                          className="w-full"
                          value={formik.values.username}
                          onChange={formik.handleChange}
                          required
                          type="text"
                        />
                      </div>
                      <div className="mb-2 p-input-icon-left w-full">
                        <i className="pi pi-lock"></i>
                        <Password
                          name="password"
                          id="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          label="Password"
                          inputClassName="w-full"
                          feedback={false}
                          toggleMask
                          className="w-full"
                          placeholder={PASSWORD}
                          required
                        />
                      </div>
                      <div className="flex justify-content-between align-items-center my-2">
                        <div className="field-checkbox">
                          <Checkbox
                            inputId="rememberme"
                            checked={rememberUser}
                            onChange={(e) => setRememberUser(e.checked)}
                          />
                          <label
                            className="text-sm text-500"
                            htmlFor="rememberme"
                          >
                            {REMEMBER_ME}
                          </label>
                        </div>
                        <div>
                          <Link
                            to="/forgotpassword"
                            className="p-button p-button-danger p-button-text"
                          >
                            {RESET_PASSWORD}
                          </Link>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {connection === "OFFLINE" && fingerprint ? (
                        <>
                          <div className="mb-4 p-input-icon-right w-full">
                            <InputText
                              name="fingerprint"
                              id="fingerprint"
                              label="Fingerprint"
                              placeholder={FINGER_PRINT}
                              className="w-full"
                              value={fingerprint}
                              disabled
                              type="text"
                            />
                            <i
                              className={copyIconClass}
                              style={{
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                navigator.clipboard.writeText(fingerprint);
                                setCopyIconClass("pi pi-check");
                                setTimeout(
                                  () => setCopyIconClass("pi pi-clone"),
                                  1000
                                );
                              }}
                            ></i>
                          </div>
                          <div className="mb-2">
                            <span className="font-bold">
                              {OFFLINE_MODE}
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="font-bold">
                              {CONTACT_ADMINISTRATOR_LICENSE}
                              </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mb-4 p-input-icon-left w-full">
                            <i className="pi pi-key"></i>
                            <InputText
                              name="licenseKey"
                              id="licenseKey"
                              label="License Key"
                              placeholder={LICENSE_KEY}
                              className="w-full"
                              value={formik.values.licenseKey}
                              onChange={formik.handleChange}
                              required
                              type="text"
                            />
                          </div>
                          <div className="mb-4 p-input-icon-left w-full">
                            <i className="pi pi-map-marker"></i>
                            <InputText
                              name="ipAddress"
                              id="ipAddress"
                              label="IP Address"
                              placeholder={IP_ADDRESS}
                              className="w-full"
                              value={formik.values.ipAddress}
                              onChange={formik.handleChange}
                              required
                              type="text"
                            />
                          </div>
                          <div className="mb-4 p-input-icon-left w-full">
                            <i className="pi pi-database"></i>
                            <InputText
                              name="dbUrl"
                              id="dbUrl"
                              label="DB URL"
                              placeholder={DB_URL}
                              className="w-full"
                              value={formik.values.dbUrl}
                              onChange={formik.handleChange}
                              required
                              type="text"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

{formSubmitted && initialLoadComplete && !validationInProgress && errorMsg && (
  <Message text={errorMsg} severity="error" />
)}
                  {!validationInProgress && (
                    <div className="text-center license-uploader">
                      {connection === "OFFLINE" &&
                      fingerprint &&
                      !areSMTPDetailsNeeded &&
                      !isLicenseKeyValid ? (
                        <LicenseFileUploader
                          onUploadStart={() => {
                            setValidationInProgress(true);
                          }}
                          onUploadSuccess={(res) =>
                            validationCheckSuccess(res, true)
                          }
                          onUploadErr={(e) => {
                            const data = get(e, "xhr.response", `'""'`);
                            validationCheckError({
                              response: {
                                data: JSON.parse(data),
                              },
                            }).catch(errorHandler);
                          }}
                        />
                      ) : (
                        <Button
                          label={
                            areSMTPDetailsNeeded
                              ? REGISTER
                              : isLicenseKeyValid
                              ? LOGIN
                              : connection === "OFFLINE" && fingerprint
                              ? UPLOAD_AND_VALIDATE
                              : VALIDATE
                          }
                          loading={loading}
                          disabled={loading}
                          icon="pi pi-lock-open"
                          className="p-button-lg p-button-raised w-full p-button-primary"
                          type="submit"
                        ></Button>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
